import React from "react";
import { Bullets, CTACard, Lead, P, Section } from "../../../../blog/components";
import ctaBg from "../../../../assets/demo/screen-01.png";

export const title = "Diseñar un bucle de agente para programar";
export const excerpt =
  "Si alguna vez has vuelto a pegar una prueba fallida en un agente de programación, tú eras el bucle. Apuntes sobre cómo diseñar ese bucle a propósito.";
export const heroAlt =
  "Un río sinuoso cruza una amplia llanura verde hacia el horizonte al atardecer";
export const readingTime = "6 min de lectura";

const BLUE = "#5ba8f5";
const FAIL = "#e0795f";
/** Handwriting face (loaded in index.html) with safe fallbacks, for the sketched figures. */
const HAND = '"Architects Daughter", "Comic Sans MS", "Segoe Print", cursive';
/** Asymmetric border-radii that read as a hand-drawn rectangle; rotate through them for variety. */
const WONKY = [
  "255px 15px 225px 15px / 15px 225px 15px 255px",
  "15px 225px 18px 255px / 255px 18px 225px 15px",
  "225px 15px 255px 18px / 18px 255px 15px 225px",
  "15px 255px 15px 225px / 225px 15px 255px 18px",
];
const wonk = (i: number) => WONKY[((i % WONKY.length) + WONKY.length) % WONKY.length];
const tilt = (i: number) => [-0.6, 0.5, -0.4, 0.7, -0.3][i % 5] ?? 0;

function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/** A hand-drawn stage box in the loop diagram. */
function LoopBox({ i, title, note, accent }: { i: number; title: string; note: string; accent?: string }) {
  return (
    <div
      style={{
        padding: "10px 16px 12px",
        borderRadius: wonk(i),
        border: `2px solid ${accent ?? "var(--border-strong)"}`,
        background: "var(--surface-5)",
        transform: `rotate(${tilt(i) * 0.5}deg)`,
        textAlign: "center",
      }}
    >
      <div style={{ fontFamily: HAND, fontSize: 17, color: "var(--text-primary)" }}>{title}</div>
      <div style={{ fontFamily: HAND, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.4, marginTop: 1 }}>{note}</div>
    </div>
  );
}

/** A short sketched arrow pointing down, optionally rotated for a fork. */
function LoopArrow({ i = 0, angle = 0, label, labelColor }: { i?: number; angle?: number; label?: string; labelColor?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 7, height: 38 }}>
      <svg width="18" height="30" viewBox="0 0 18 30" aria-hidden style={{ flexShrink: 0, transform: `rotate(${angle + tilt(i)}deg)` }}>
        <path d="M9 2 C 7.5 8, 10.5 13, 9 20" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" />
        <path d="M4.5 19 L9 26 L13.5 19" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {label && <span style={{ fontFamily: HAND, fontSize: 13.5, color: labelColor ?? "var(--text-muted)" }}>{label}</span>}
    </div>
  );
}

/**
 * The bare loop as a one-line sketch: the pipeline of words, plus a retry arc
 * from decide back up into attempt.
 */
function LoopStripFigure() {
  const arrow = (x: number, i: number) => (
    <g transform={`translate(${x} 0) rotate(${tilt(i) * 2} 12 37)`}>
      <path d="M1 37 C 8 35.5, 16 38.5, 23 37" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M16 32 L23 37 L16 42" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
  const word = (x: number, w: string, i: number) => (
    <text x={x} y={42} fontFamily={HAND} fontSize="17" fill="var(--text-primary)" transform={`rotate(${tilt(i) * 0.4} ${x} 42)`}>
      {w}
    </text>
  );
  return (
    <figure style={{ margin: "26px 0" }}>
      <div
        role="img"
        aria-label="Petición, definir objetivo, intento, verificar, decidir, con un arco de reintento que vuelve desde decidir hasta intento, llevando la evidencia."
        style={{
          padding: "10px 14px 4px",
          borderRadius: wonk(1),
          border: "2px solid var(--text-muted)",
          background: "var(--surface-4)",
        }}
      >
        <svg viewBox="0 0 500 138" style={{ display: "block", width: "100%", maxWidth: 560, height: "auto", margin: "0 auto" }} aria-hidden>
          {word(10, "petición", 0)}
          {arrow(76, 0)}
          {word(106, "definir objetivo", 1)}
          {arrow(206, 1)}
          {word(236, "intento", 2)}
          {arrow(308, 2)}
          {word(338, "verificar", 3)}
          {arrow(394, 3)}
          {word(424, "decidir", 4)}

          <path
            d="M452 56 C 458 80, 428 93, 357 94 C 296 95, 275 88, 271 64"
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth="1.8"
            strokeLinecap="round"
            opacity="0.85"
          />
          <path d="M264 69 L271 60 L278 69" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
          <text x="360" y="122" fontFamily={HAND} fontSize="13.5" fill="var(--text-secondary)" textAnchor="middle" transform="rotate(-0.5 360 122)">
            reintentar, llevando la evidencia
          </text>
        </svg>
      </div>
    </figure>
  );
}

/**
 * The loop, sketched vertically: goal + check → attempt → observe → verify,
 * a loop-back gutter on the right carrying the failure evidence, and the two
 * terminal states at the bottom. Same hand-drawn look as the rest of the blog.
 */
function AgentLoopFigure() {
  return (
    <figure style={{ margin: "34px 0" }}>
      <div
        role="img"
        aria-label="El bucle del agente de programación. Un objetivo y una comprobación conducen a un intento, una observación y una verificación. Una comprobación fallida vuelve a un nuevo intento llevando la evidencia. Si pasa, termina en trabajo verificado; si el bucle se atasca, devuelve el control a una persona."
        style={{
          padding: "26px 20px",
          borderRadius: wonk(0),
          border: "2px solid var(--text-muted)",
          background: "var(--surface-4)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) clamp(64px, 16vw, 104px)",
            columnGap: 8,
            maxWidth: 480,
            margin: "0 auto",
          }}
        >
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={0} accent={BLUE} title="Objetivo + comprobación" note="qué significa terminado, y la evidencia que lo demuestra" />
          </div>
          <div style={{ gridColumn: 1 }}><LoopArrow i={0} /></div>
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={1} title="Intento" note="el agente modifica un worktree aislado" />
          </div>
          <div style={{ gridColumn: 1 }}><LoopArrow i={1} /></div>
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={2} title="Observar" note="recoge el diff, la salida de las pruebas y el estado en ejecución" />
          </div>
          <div style={{ gridColumn: 1 }}><LoopArrow i={2} /></div>
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={3} title="Verificar" note="contrasta la evidencia con la comprobación" />
          </div>

          {/* Loop-back gutter: from Verify up into Attempt, carrying the evidence. */}
          <div aria-hidden style={{ gridColumn: 2, gridRow: "3 / 8", position: "relative", margin: "26px 0" }}>
            <div
              style={{
                position: "absolute",
                inset: "0 6px 0 6px",
                border: "2px solid var(--text-muted)",
                borderLeft: "none",
                borderRadius: "0 30px 34px 0 / 0 44px 38px 0",
                opacity: 0.8,
              }}
            />
            <svg width="14" height="16" viewBox="0 0 14 16" style={{ position: "absolute", left: -4, top: -7 }}>
              <path d="M12 2 L2 8 L12 14" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
            </svg>
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: -4,
                transform: "translateY(-50%)",
                background: "var(--surface-4)",
                padding: "6px 0",
                fontFamily: HAND,
                fontSize: 13,
                lineHeight: 1.4,
                color: "var(--text-secondary)",
                textAlign: "center",
                width: "min(100%, 96px)",
              }}
            >
              todavía no, otra vuelta con la evidencia
            </span>
          </div>

          <div style={{ gridColumn: 1, display: "flex", justifyContent: "space-evenly" }}>
            <LoopArrow i={3} angle={26} label="pasa" labelColor={BLUE} />
            <LoopArrow i={4} angle={-26} label="atascado" labelColor={FAIL} />
          </div>
          <div style={{ gridColumn: 1, display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div style={{ flex: "1 1 170px" }}>
              <LoopBox i={2} accent={BLUE} title="Trabajo verificado" note="tuyo para revisar, fusionar o descartar" />
            </div>
            <div style={{ flex: "1 1 170px" }}>
              <LoopBox i={3} accent={FAIL} title="De vuelta a una persona" note="bloqueado, sin presupuesto o repitiéndose" />
            </div>
          </div>
        </div>
      </div>
      <figcaption
        style={{ marginTop: 12, fontFamily: HAND, fontSize: 16, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.5 }}
      >
        El agente solo ocupa la casilla de Intento. El bucle que lo rodea decide qué cuenta como terminado, en qué evidencia confiar y cuándo parar.
      </figcaption>
    </figure>
  );
}

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "S. Macedo, «Stop Hand-Holding Your Coding Agent: Engineering the Loops that Replace Step-by-Step Prompting» (2026). Usado para la distinción entre los ciclos internos del agente y las especificaciones de bucle externas, y para la estructura de disparador, objetivo, verificación, parada y memoria. Es un preprint reciente basado en parte en un corpus codificado a mano de 50 bucles públicos.",
    href: "https://arxiv.org/abs/2607.00038",
    label: "arxiv.org/abs/2607.00038",
  },
  {
    text: "H. Guan et al., «SWE-Cycle: Benchmarking Code Agents across the Complete Issue Resolution Cycle» (2026). Usado para la separación entre reconstrucción del entorno, implementación, generación de pruebas de verificación y ejecución del ciclo completo.",
    href: "https://arxiv.org/abs/2605.13139",
    label: "arxiv.org/abs/2605.13139",
  },
  {
    text: "R. Wang et al., «SWE-Review: Closing the Loop on Issue Resolution with Agentic Code Review» (2026). Usado para la arquitectura de generar-revisar-corregir y el valor reportado de un feedback de revisión estructurado. Es un preprint muy reciente.",
    href: "https://arxiv.org/abs/2607.06065",
    label: "arxiv.org/abs/2607.06065",
  },
  {
    text: "S. Shrivastava, «Semantic Early-Stopping for Iterative LLM Agent Loops» (2026). Usado para la crítica a las iteraciones fijas y el resultado del 38 % de tokens operativos. El experimento cubre 60 preguntas de QA multisalto, no tareas de programación, así que se presenta solo como ejemplo de política de parada.",
    href: "https://arxiv.org/abs/2606.27009",
    label: "arxiv.org/abs/2606.27009",
  },
  {
    text: "X. Hou et al., «When Agents Do Not Stop: Uncovering Infinite Agentic Loops in LLM Agents» (2026). Usado para la definición y el escaneo de repositorios de bucles agénticos infinitos.",
    href: "https://arxiv.org/abs/2607.01641",
    label: "arxiv.org/abs/2607.01641",
  },
  {
    text: "0-AI-UG, «Cate Agent: job-card agent that drives coding CLIs in canvas worktrees» y la implementación de la herramienta Cate Agent (2026). Usado para el flujo de objetivo, iteración, worktree, driver, verificador y selección de resultados de Cate.",
    href: "https://github.com/0-AI-UG/cate/pull/423",
    label: "github.com/0-AI-UG/cate/pull/423",
  },
];

export default function Post() {
  return (
    <>
      <Lead>Una instrucción le dice a un agente de programación qué hacer una vez. Casi todo lo que importa ocurre después de que el primer resultado vuelva mal.</Lead>

      <P>
        Supón que le pides a un agente de programación que añada exportación a CSV a una página de informes. Lee el repositorio,
        edita unos cuantos archivos, ejecuta una prueba e informa de que ha tenido éxito. La prueba falla, así que le vuelves a pegar el error.
        La siguiente versión rompe la página, así que le dices que abra el navegador y mire. La versión
        de después funciona, pero también ha reescrito de paso la mitad del módulo de informes.
      </P>
      <P>
        Para la tercera ronda, tú eres el bucle. Llevas la evidencia de un intento a otro y decides
        cuándo el trabajo está realmente terminado.
      </P>
      <P>
        La pregunta que me interesa es cuánto de eso se puede escribir con la precisión suficiente para que el
        sistema lo ejecute solo, y dónde no se puede, de modo que el trabajo vuelva a una persona en lugar
        de dar vueltas para siempre.
      </P>

      <Section title="El bucle fuera del agente">
        <P>
          La palabra <em>bucle</em> hace mucho trabajo en ese último párrafo, así que conviene separar los
          sentidos. Está el bucle corriente dentro de un programa. Está el ciclo interno que la mayoría de los
          agentes ya ejecutan por su cuenta: mirar el entorno, elegir una acción, llamar a una herramienta, mirar
          el resultado, seguir.
        </P>
        <P>El que me importa aquí es un tercer bucle, y queda fuera del modelo:</P>
        <LoopStripFigure />
        <P>
          Un preprint reciente lo llama <em>especificación de bucle</em>: un disparador, un objetivo, algo de ejecución, un
          paso de verificación, una regla para cuándo parar y memoria que se lleva entre rondas.<Fn n={1} /> El
          nombre es nuevo, pero ninguna de las partes lo es. La integración continua lleva años haciendo una versión de esto.
          Se ejecuta cuando pasa algo, comprueba el resultado, registra lo que vio y termina en un
          estado con nombre. Lo único que añade un agente es un modelo capaz de reescribir su propio plan entre ejecuciones.
        </P>
      </Section>

      <Section title="Empieza por la condición de parada">
        <P>
          «Implementa la exportación a CSV» describe una actividad. No describe cuándo la actividad está completa.
        </P>
        <Bullets
          items={[
            <><strong>Objetivo:</strong> un usuario puede exportar el informe filtrado en ese momento como un archivo CSV correctamente codificado.</>,
            <><strong>Comprobación:</strong> ejecutar las pruebas de exportación, verificar el build e inspeccionar el diff en busca de cambios fuera de la función de informes.</>,
          ]}
        />
        <P>
          La separación es lo importante. El objetivo es lo que debería volverse verdad. La comprobación es la evidencia que el
          bucle usará de verdad para decidir si lo consiguió.
        </P>
        <P>
          Junta las dos y el agente acaba siendo su propio autor, tester y juez. Cantará victoria
          encantado porque el código parece razonable, porque un comando salió con cero, o porque
          simplemente se ha quedado sin ideas. Nada de eso significa que la función funcione.
        </P>
        <P>
          Conviene tratar la verificación como una escalera y no como una única puerta. Un autoinforme del agente
          es el peldaño barato de abajo, y por sí solo no vale casi nada. Una comprobación estática limpia y un
          build que pasa te dicen más. Unas pruebas específicas te dicen más todavía, porque ejercitan el comportamiento
          que de verdad pediste. Ejecutar el producto real saca a la luz los fallos de integración que ninguna prueba unitaria
          iba a atrapar, y una revisión aparte recoge las cosas que el agente de implementación se ha
          entrenado calladamente para saltarse.
        </P>
        <P>
          La investigación reciente sobre agentes de programación empuja en la misma dirección. SWE-Cycle separa la preparación del entorno,
          la implementación y la generación de pruebas, y luego mide una caída brusca de rendimiento en cuanto los agentes tienen que
          ejecutar el ciclo entero por sí mismos.<Fn n={2} /> SWE-Review acopla un revisor aparte a un
          bucle de generar-revisar-corregir y descubre que un feedback de revisión estructurado hace que las correcciones posteriores sean mejores
          que la generación de un solo tiro.<Fn n={3} />
        </P>
        <P>
          El verificador no tiene por qué ser otro modelo. Cuando una prueba determinista puede zanjarlo, usa la
          prueba. Un modelo se gana su sitio solo cuando la comprobación necesita criterio: ¿se quedó el diff dentro de la
          función, coincide de verdad la UI con lo que se pidió, es este apaño una concesión que aceptarías?
        </P>
      </Section>

      <Section title="La estructura de un bucle de programación">
        <P>
          Un intento cambia el entorno, la observación anota lo que pasó, y la verificación contrasta
          esa evidencia con el objetivo. Una comprobación fallida sigue uno de dos caminos: o entrega evidencia útil
          al siguiente intento, o sale a un estado terminal con nombre.
        </P>
        <AgentLoopFigure />
        <P>
          El agente es una casilla de este diagrama. Cómo se comporta el conjunto depende tanto del
          verificador, del estado que llevas entre intentos, de la política de reintentos y de las reglas que
          deciden cuándo se le permite terminar al bucle.
        </P>
      </Section>

      <Section title="Un reintento necesita información nueva">
        <P>Darle al agente la misma instrucción otra vez es técnicamente un bucle. Solo que no es un bucle que llegue a ningún sitio.</P>
        <P>
          Cada intento fallido debería entregar algo concreto al siguiente: una aserción que falla, una
          captura de pantalla, un error de compilación, un comentario de un revisor, o incluso una frase llana sobre por qué se falló el
          objetivo. La siguiente ronda debería partir de eso, no de una copia nueva de la petición original.
        </P>
        <P>
          Aislar los intentos ayuda más de lo que esperaba. Una rama o worktree nueva da a cada intento
          un punto de partida limpio, los mantiene comparables e impide que un enfoque abandonado se filtre en el
          que viene después. Ejecuta varios intentos a la vez y cada uno necesita su propio espacio de trabajo de todos modos. Entonces el
          bucle puede quedarse con el resultado que de verdad se verificó en lugar de intentar coser diffs
          medio compatibles.
        </P>
        <P>
          Con la memoria conviene ser exigente. El bucle necesita el objetivo, los veredictos anteriores, las decisiones
          que importaron y el estado actual del repositorio. Casi nunca necesita la transcripción completa
          de cada llamada a una herramienta. Guardarlo todo solo hace la siguiente ronda más larga, no más lista.
        </P>
      </Section>

      <Section title="Parar es parte de la corrección">
        <P>Todo bucle necesita estados terminales explícitos. Como mínimo:</P>
        <Bullets
          items={[
            <><strong>éxito:</strong> la comprobación pasó;</>,
            <><strong>bloqueado:</strong> avanzar requiere a una persona o información externa que falta;</>,
            <><strong>agotado:</strong> se alcanzó el límite de intentos o de coste;</>,
            <><strong>cancelado:</strong> el usuario detuvo el trabajo;</>,
            <><strong>error:</strong> el propio bucle no pudo continuar con seguridad.</>,
          ]}
        />
        <P>
          Un tope de intentos sigue mereciendo la pena, pero trátalo como un límite de seguridad y no como una definición de
          terminado. El trabajo sobre sistemas iterativos con modelos de lenguaje sugiere que los recuentos de iteraciones fijos queman esfuerzo mucho después
          de que la salida haya dejado de mejorar. En un pequeño experimento no relacionado con la programación, una regla de parada
          semántica recortó los tokens operativos un 38 por ciento con aproximadamente la misma calidad medida.<Fn n={4} /> Ese número
          no se traslada tal cual al software, pero sí lo hace la lección de fondo: vigila si el bucle está
          avanzando, no solo cuántas vueltas ha dado.
        </P>
        <P>
          El fallo contrario es el bucle que nunca llega a un límite real. Un estudio de análisis estático de julio de 2026
          examinó 6.549 repositorios de agentes y confirmó 68 defectos probables de bucle de agente infinito en
          47 proyectos.<Fn n={5} /> En cada caso el bucle seguía llegando a llamadas al modelo, herramientas, reintentos o traspasos
          sin ninguna condición de parada que cubriera todo el camino de retroalimentación.
        </P>
        <P>
          Para programar, una regla de no-progreso viable podría saltar cuando dos intentos fallan la misma comprobación por la misma
          razón, cuando el diff deja de cambiar de forma significativa, o cuando el siguiente paso necesitaría una autoridad
          que al agente nunca se le dio.
        </P>
      </Section>

      <Section title="Un bucle debe seguir siendo inspeccionable">
        <P>
          Entregar más trabajo a la máquina no reduce cuánto importa su estado interno. Convierte
          ese estado en casi lo único que todavía puedes supervisar.
        </P>
        <P>
          Mientras un bucle se ejecuta, un desarrollador debería poder responder a preguntas llanas sobre él. ¿Cuál es el objetivo
          ahora mismo? ¿Qué intento está en marcha? ¿Qué ha cambiado? ¿Qué comprobación se está ejecutando? ¿Por qué falló el último intento,
          y qué pasa si este pasa?
        </P>
        <P>
          Esa es la idea detrás del nuevo Cate Agent. Para una tarea de código, un orquestador de solo lectura fija el objetivo y
          la comprobación de verificación. Puede lanzar uno o más intentos en worktrees de Git nuevos. Los drivers por intento
          arrancan CLIs de agentes de programación en terminales reales del canvas, y cuando un intento termina un verificador aparte
          lo comprueba. A partir de ahí, el orquestador o bien toma un resultado que pasa o bien inicia otra ronda desde la
          evidencia del fallo. El trabajo terminado se queda en un estado de revisión hasta que lo fusionas, lo abres como una pull request
          o lo tiras.<Fn n={6} />
        </P>
        <P>
          La razón de que el bucle siga siendo legible es que cada parte tiene un sitio que puedes señalar. Los intentos viven en
          sus propios worktrees y terminales. La verificación tiene su propio terminal. El resultado que conservaste queda
          visiblemente separado de los intentos que rechazaste. El canvas despliega el proceso como un espacio de trabajo en lugar
          de aplanarlo en un único chat con scroll.
        </P>
        <P>
          Así que diseñar un bucle resulta ser sobre todo trabajo estructural. Le das a un proceso repetido una forma:
          algo concreto que satisfacer, evidencia con la que comprobarlo, un poco de estado que llevar hacia delante, límites
          que de verdad lo detengan y un final que una persona todavía pueda leer. Conseguir que el agente corra más tiempo es
          casi lo de menos.
        </P>
        <P>
          El modelo hace los pasos individuales. Que esos pasos sumen software que de verdad puedas lanzar es una
          propiedad del bucle que construiste a su alrededor, y esa parte sigue siendo cosa tuya.
        </P>
      </Section>

      <CTACard download github bgImage={ctaBg} />

      <Section title="Referencias">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
